import { Button } from "wiz-front-shared/components/button/Button";
import { Input, InputInfoMsg } from "components/common/input/Input";
import { ReactComponent as IcCheck } from "wiz-front-shared/assets/icon/ic_check.svg";
import profileDefaultImg from "assets/images/profile_default.png";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next"
import { signUpDataAtom, pushStepAtom } from "store/signUpStore";
import { userStore } from "store/userStore";
import { formatCtnForDisplay } from "utils/phoneNumber";
import './SignUpStep6.css'

// Step6: 사용자 정보 입력 창 (이메일, 비밀번호, 닉네임)
// 결과에 따라 Step7(중복), Step8(실패), Step9(성공)로 이동
export const SignUpStep6 = () => {
    const {t} = useTranslation();
    const pushStep = useSetAtom(pushStepAtom);
    const signUpData = useAtomValue(signUpDataAtom);
    const signupState = useAtomValue(userStore.signup.stateAtom);
    const checkDuplicateEmailState = useAtomValue(userStore.checkDuplicateEmail.stateAtom);
    const emailDebounceRef = useRef<NodeJS.Timeout | null>(null);
    // 마지막으로 중복검사를 요청한 이메일 — 응답이 현재 입력값에 대한 것인지 매칭 (race 방지)
    const emailReqRef = useRef<string>("");

    // Refs
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const nickNameRef = useRef<HTMLInputElement>(null);

    //input
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [nickName, setNickName] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    //input Info Msg
    const [emailInfoMsg, setEmailInfoMsg] = useState<InputInfoMsg>();
    const [passwordInfoMsg, setPasswordInfoMsg] = useState<InputInfoMsg>();
    const [nickNameInfoMsg, setNickNameInfoMsg] = useState<InputInfoMsg>();

    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isPwdValid, setIsPwdValid] = useState(false);
    const [isNickNameValid, setIsNickNameValid] = useState(false);

    // SNS 로그인 여부 및 SNS에서 받아온 값 확인
    const isSnsLogin = signUpData.snsLogin === true;
    const hasSnsEmail = signUpData.snsProvidedFields.email;

    // SNS 프로필 이미지 — URL 이 있을 때만 미리보기와 사용 여부 선택을 노출한다.
    //   이미지 로드에 실패하면(URL 만료/네트워크) 선택할 수 없는 항목이므로 영역 자체를 숨긴다.
    const [useSnsProfileImg, setUseSnsProfileImg] = useState(signUpData.useSnsProfileImg);
    const [isProfileImgBroken, setIsProfileImgBroken] = useState(false);
    const snsProfileImgUrl = signUpData.snsProfileImgUrl;
    const showProfileImgOption = isSnsLogin && !!snsProfileImgUrl && !isProfileImgBroken;

    // Step6 진입 시 SNS에서 받아온 값 초기화
    useEffect(() => {
        if (hasSnsEmail && !email) {
            setEmail(signUpData.email);
            setIsEmailValid(true);
        }
        if (isSnsLogin && signUpData.nickname && !nickName) {
            setNickName(signUpData.nickname);
        }
    }, []);

    // Step6 진입 시 자동 포커스
    useEffect(() => {
        const timer = setTimeout(() => {
            if (isSnsLogin) {
                // SNS 로그인: 이메일 readonly → 닉네임에 포커스
                nickNameRef.current?.focus();
            } else {
                emailRef.current?.focus();
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [isSnsLogin]);

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    const checkPwdValid = () => {
        const isPwdLengthValid = password.length >= 8;
        const isPwdRegexValid = (/^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/).test(password);
        const isPwdConfirmValid = password === confirmPassword;
        const isPwdValid = isPwdLengthValid && isPwdRegexValid && isPwdConfirmValid;

        setPasswordInfoMsg(
            isPwdLengthValid ?
                isPwdRegexValid ?
                    isPwdConfirmValid ?
                        undefined :
                        {
                            msg: t("signUp.step6.passwordNotMatch"),
                            type: "error"
                        } :
                    {
                        msg: t("signUp.step6.passwordErrorMsg2"),
                        type: "error"
                    } :
                {
                    msg: t("signUp.step6.passwordErrorMsg1"),
                    type: "error"
                }
        );
        setIsPwdValid(isPwdValid);
    }

    useEffect(() => {
        // SNS에서 받아온 이메일인 경우 중복 체크 건너뜀
        if (hasSnsEmail) {
            setEmailInfoMsg(undefined);
            setIsEmailValid(true);
            return;
        }

        if(email.trim().length <= 0) {
            setEmailInfoMsg(undefined);
            // SNS 로그인일 때 이메일이 비어있으면 유효한 것으로 처리
            setIsEmailValid(isSnsLogin);
            return;
        }

        const isFormatValid = emailRegex.test(email);
        if (!isFormatValid) {
            setEmailInfoMsg({
                msg: t("signUp.step6.emailErrorMsg"),
                type: "error"
            });
            setIsEmailValid(false);
            return;
        }

        // 이메일 형식이 맞으면 디바운싱 후 중복 체크 API 호출
        setEmailInfoMsg({
            msg: t("signUp.step6.emailChecking"),
            type: "error"
        });

        if (emailDebounceRef.current) {
            clearTimeout(emailDebounceRef.current);
        }

        emailDebounceRef.current = setTimeout(() => {
            emailReqRef.current = email.trim();
            userStore.onCheckDuplicateEmail(email.trim(), signUpData.serviceId);
        }, 300);

        return () => {
            if (emailDebounceRef.current) {
                clearTimeout(emailDebounceRef.current);
            }
        };
    },[email, isSnsLogin, hasSnsEmail])

    // 이메일 중복 체크 결과 처리
    useEffect(() => {
        const status = checkDuplicateEmailState.status;
        if (status !== "ready" && status !== "error") return;
        // 응답이 현재 입력값(=마지막 요청)에 대한 것이 아니면 무시 — 빠르게 값을 바꿨을 때
        //   늦게 도착한 이전 요청 응답이 잘못된 사용가능/중복 표시를 덮어쓰는 것을 방지.
        if (email.trim() !== emailReqRef.current) return;
        if (checkDuplicateEmailState.status === "ready") {
            const result = checkDuplicateEmailState.data?.result;
            if (result === "success") {
                setEmailInfoMsg({
                    msg: t("signUp.step6.emailAvailable"),
                    type: "passed"
                });
                setIsEmailValid(true);
            } else if (result === "duplicate") {
                setEmailInfoMsg({
                    msg: t("signUp.step6.emailDuplicate"),
                    type: "error"
                });
                setIsEmailValid(false);
            }
        } else if (checkDuplicateEmailState.status === "error") {
            setEmailInfoMsg({
                msg: t("signUp.step6.emailCheckFailed"),
                type: "error"
            });
            setIsEmailValid(false);
        }
        // email 은 deps 에서 제외 — 응답(status/data) 도착 시점에만 평가하면 됨 (클로저로 최신값 참조)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkDuplicateEmailState.status, checkDuplicateEmailState.data])

    useEffect(() => {
        if(password.length <= 0) {
            setIsPwdValid(false);
            setPasswordInfoMsg(undefined);
            return;
        }
        checkPwdValid();
    },[password, confirmPassword]);

    useEffect(() => {
        if(nickName.trim().length <= 0) {
            setNickNameInfoMsg(undefined);
            setIsNickNameValid(false);
        } else {
            const isNickNameLengthValid = nickName.length <= 20;
            setNickNameInfoMsg(
                isNickNameLengthValid ?
                    undefined :
                    {
                        msg: t("signUp.step6.nickNameErrorMsg2"),
                        type: "error"
                    }
            );
            setIsNickNameValid(isNickNameLengthValid);
        }
    },[nickName])

    // 회원가입 API 상태 감지
    useEffect(() => {
        if (signupState.status === "pending") {
            setIsLoading(true);
        // 성공 → Step9
        } else if (signupState.status === "ready") {
            setIsLoading(false);
            pushStep(9);
        // 기타 실패 → Step8
        } else if (signupState.status === "error") {
            setIsLoading(false);
            pushStep(8);
        }
    }, [signupState.status, signupState.error, pushStep]);

    // 회원가입 요청
    const handleSignUp = () => {
        // 회원가입 API에서 사용하지 않는 필드 제거
        //   보호자 인증 5항목(parentName/Telecom/Birth/Gender/Ctn)은 인증 전용 — 미전송.
        //   nationality/parentNationality 는 다날 genderDigit 매핑 전용 — 미전송.
        //   ctnCountry 는 Step4 국가 picker 초기값 전용(ISO2) — ctnCountryCd 로 충분하므로 미전송.
        //   snsProfileImgUrl/useSnsProfileImg 는 화면 상태 — 사용 동의 시에만 profileImgUrl 로 변환해 전송.
        //   parentIdentityVerificationId 는 보호자 동의 증빙으로 restSignUpData 에 남겨 전송.
        //   가입 연령 정책 화면 상태(policyEnabled 등)와 보호자 입력 필드는 아래에서 규격에 맞춰 따로 싣는다.
        const {
            parentConsent, name, telecom, parentCtn,
            parentName, parentTelecom, parentBirth, parentGender,
            nationality, parentNationality, ctnCountry,
            snsProfileImgUrl: _snsProfileImgUrl, useSnsProfileImg: _useSnsProfileImg,
            snsProvidedFields,
            policyEnabled, idvEnabled: _idvEnabled, guardianRequired, policyBlockReason: _policyBlockReason,
            consentAge: _consentAge, infoConfirmed, verifyId,
            guardianName, guardianRelationship, guardianCtn, guardianCtnCountryCd,
            ...restSignUpData
        } = signUpData;

        // 가입 연령 정책 적용 서비스 — 이름·실정보 확인·휴대폰 인증 ID, 동의 대상이면 보호자 정보
        //   (정책 미적용이면 기존과 동일하게 싣지 않는다)
        const policyFields = policyEnabled ? {
            infoConfirmed,
            ...(verifyId != null ? { verifyId } : {}),
            ...(guardianRequired ? {
                name,
                guardian: {
                    name: guardianName,
                    relationship: guardianRelationship,
                    ctn: guardianCtn,
                    ctnCountryCd: guardianCtnCountryCd,
                },
            } : {}),
        } : {};

        // 입력받은 값만 추가해서 전송
        const signUpRequest = {
            ...restSignUpData,
            ...policyFields,
            email: email.trim() || undefined,
            password: isSnsLogin ? undefined : password,
            nickname: nickName.trim(),
            // 사용에 동의한 경우에만 전송 — 서버가 이 URL 을 내려받아 프로필 이미지로 등록한다.
            //   미동의/미제공/로드실패 시에는 키 자체를 넣지 않는다.
            ...(showProfileImgOption && useSnsProfileImg && snsProfileImgUrl
                ? { profileImgUrl: snsProfileImgUrl }
                : {}),
        };

        userStore.onSignup(signUpRequest);
    };

    return (
        <div className="sign-up-step-6">
            {/* SNS 프로필 이미지 — SNS 가 제공했고 로드에 성공한 경우에만 노출.
                제공 동의와 별개로 우리 서비스 프로필로 쓸지는 사용자가 정하므로,
                토글이 아닌 두 선택지를 명시한 탭으로 제시한다 (native CustomTab 과 동일한 형태).
                프로필을 구성하는 항목이라 입력 폼 최상단에 둔다. */}
            {showProfileImgOption && (
                <div className="profile-img-section">
                    {/* 다른 입력 항목과 동일한 라벨 스타일 (Input 의 common-input-title 재사용) */}
                    <div className="common-input-title-container">
                        <span className="common-input-title">{t("signUp.step6.profileImg")}</span>
                    </div>

                    {/* 탭하면 SNS 사진 / 기본 이미지가 전환된다. 지금 보이는 사진이 곧 등록될 사진이라
                        별도 설명 없이도 선택 결과가 드러난다. */}
                    <div className="profile-img-body">
                        <button
                            type="button"
                            className={`profile-img-avatar ${useSnsProfileImg ? "is-on" : "is-off"}`}
                            onClick={() => setUseSnsProfileImg(v => !v)}
                            aria-pressed={useSnsProfileImg}
                            aria-label={t("signUp.step6.useProfileImg")}
                        >
                            <img
                                src={useSnsProfileImg ? snsProfileImgUrl : profileDefaultImg}
                                alt={t("signUp.step6.profileImg")}
                                onError={() => setIsProfileImgBroken(true)}
                            />
                            {/* 체크박스와 동일한 아이콘 사용 — 앱 전반의 '선택됨' 표시를 일치시킨다 */}
                            <span className="profile-img-check" aria-hidden="true">
                                <IcCheck className="profile-img-check-icon" />
                            </span>
                        </button>
                        <span className="profile-img-caption">
                            {useSnsProfileImg ? t("signUp.step6.useProfileImg") : t("signUp.step6.useDefaultImg")}
                        </span>
                    </div>
                </div>
            )}

            {/* CTN (전화번호) - 읽기 전용.
                저장 형식(선행 0 없는 national number)은 사용자가 자기 번호로 알아보기 어려워
                각 국가의 국내 표기로 포맷해 보여준다. 전송 값(signUpData.ctn)은 그대로다. */}
            <Input
                title={t("signUp.step6.ctn")}
                inputVal={formatCtnForDisplay(signUpData.ctn, signUpData.ctnCountry)}
                id="ctn"
                type="tel"
                readOnly
                showClearButton={false}
                className="sns-readonly"
            />

            {/* 이메일
                - 일반 가입: 입력 (필수)
                - SNS + 이메일 제공: readonly 표시
                - SNS + 이메일 미제공: 활용처가 없어 숨김 (빈 readonly 칸 노출 방지) */}
            {(!isSnsLogin || hasSnsEmail) && (
                <Input
                    ref={emailRef}
                    title={t("signUp.step6.email")}
                    inputVal={email}
                    setInputVal={isSnsLogin ? undefined : setEmail}
                    id="email"
                    type="email"
                    infoMsg={emailInfoMsg}
                    placeholder={isSnsLogin ? "" : t("signUp.step6.emailPlaceHolder")}
                    isRequired={!isSnsLogin}
                    readOnly={isSnsLogin}
                    showClearButton={!isSnsLogin}
                    className={isSnsLogin ? "sns-readonly" : ""}
                    // 백엔드 tb_user_credential.email 컬럼이 varchar(100) — DB 제약에 맞춰 입력 제한
                    maxLength={100}
                />
            )}

            {/* 비밀번호 - SNS 로그인일 때는 숨김 */}
            {!isSnsLogin && (
                <>
                    <Input
                        ref={passwordRef}
                        title={t("signUp.step6.password")}
                        inputVal={password}
                        setInputVal={setPassword}
                        id="password"
                        type="password"
                        notShowInfo
                        placeholder={t("signUp.step6.passwordPlaceHolder")}
                        containerStyle={{padding: "10px 0 0"}}
                        isRequired
                    />
                    <Input
                        inputVal={confirmPassword}
                        setInputVal={setConfirmPassword}
                        id="passwordConfirm"
                        type="password"
                        placeholder={t("signUp.step6.passwordConfirmPlaceHolder")}
                        infoMsg={passwordInfoMsg}
                    />
                </>
            )}

            {/* 닉네임 */}
            <Input
                ref={nickNameRef}
                title={t("signUp.step6.nickName")}
                inputVal={nickName}
                setInputVal={setNickName}
                id="nickName"
                type="text"
                infoMsg={nickNameInfoMsg}
                placeholder={t("signUp.step6.nickNamePlaceHolder")}
                isRequired
            />

            <div className="sign-up-bottom-btn">
                <Button
                    text={isLoading ? t("signUp.loading") : t("signUp.btnSignUpCom")}
                    disabled={!isEmailValid || (!isSnsLogin && !isPwdValid) || !isNickNameValid || isLoading}
                    onPress={handleSignUp}
                />
            </div>
        </div>
    )
}
